import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Paper,
  Stack,
  Chip,
  InputAdornment,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  Campaign as CampaignIcon,
  Send as SendIcon,
  NotificationsActive as NotificationsActiveIcon,
  Link as LinkIcon,
  PhoneIphone as PhoneIphoneIcon,
} from "@mui/icons-material";
import notificationApi from "Service/Notification.api";
import { useDispatch } from "react-redux";
import { setAlertData } from "../../Redux/Screen/Action";
import ImgPath from "Constants/Imgs";

interface BroadcastNotificationDialogProps {
  open: boolean;
  onClose: () => void;
}

const BroadcastNotificationDialog: React.FC<BroadcastNotificationDialogProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useDispatch();
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [link, setLink] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      dispatch(
        setAlertData({
          alertMessage: "Please provide both a Title and Message for the broadcast.",
          alertVariant: "error",
          openAlert: true,
        })
      );
      return;
    }

    setLoading(true);
    try {
      const res = await notificationApi.broadcastNotification({
        title: title.trim(),
        message: message.trim(),
        link: link.trim() || undefined,
      });

      if (res?.success || res?.status === 200) {
        dispatch(
          setAlertData({
            alertMessage: "🚀 Broadcast push notification successfully sent to all mobile users!",
            alertVariant: "success",
            openAlert: true,
          })
        );
        setTitle("");
        setMessage("");
        setLink("");
        onClose();
      } else {
        dispatch(
          setAlertData({
            alertMessage: res?.message || "Failed to send broadcast notification.",
            alertVariant: "error",
            openAlert: true,
          })
        );
      }
    } catch (err: any) {
      console.error("Error broadcasting message:", err);
      dispatch(
        setAlertData({
          alertMessage: err?.response?.data?.message || err?.message || "Error broadcasting message.",
          alertVariant: "error",
          openAlert: true,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          p: 1,
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
          background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justify: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              bgcolor: "primary.main",
              color: "#fff",
              p: 1,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <CampaignIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="700" color="#1e293b">
              Broadcast Message to All Users
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Sends an instant push notification (Zomato / Swiggy style)
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={2.5}>
          {/* Notification Form Inputs */}
          <TextField
            label="Notification Title"
            fullWidth
            required
            placeholder="e.g. ⚡ Special Offer: 20% Off Legal Consultation!"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <NotificationsActiveIcon color="primary" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Message Body"
            fullWidth
            required
            multiline
            rows={3}
            placeholder="e.g. Talk to verified legal experts online today. Fast & confidential. Tap to consult!"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />

          <TextField
            label="Action Link (Optional URL or Screen)"
            fullWidth
            placeholder="e.g. /chat or https://nolegalpaisa.com/offers"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LinkIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {/* Live Mobile Push Preview Section */}
          <Box sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <PhoneIphoneIcon fontSize="small" color="secondary" />
              <Typography variant="subtitle2" fontWeight="700" color="#475569">
                Live Mobile Push Preview
              </Typography>
            </Stack>

            <Paper
              elevation={3}
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: "#0f172a",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.1)",
                boxShadow: "0 10px 25px rgba(0,0,0,0.3)",
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "10px",
                    bgcolor: "#fff",
                    p: 0.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={ImgPath.AppLogo || "/Assets/Imgs/nlp_logo.png"}
                    alt="Logo"
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    onError={(e: any) => {
                      e.target.style.display = "none";
                    }}
                  />
                  <NotificationsActiveIcon sx={{ color: "#1a237e", fontSize: 22 }} />
                </Box>

                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600 }}>
                      NoLegalPaisa • now
                    </Typography>
                    <Chip
                      label="PUSH"
                      size="small"
                      sx={{ height: 16, fontSize: "0.6rem", bgcolor: "#38bdf8", color: "#000", fontWeight: "bold" }}
                    />
                  </Stack>

                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "700", color: "#f8fafc", mt: 0.5, lineHeight: 1.2 }}
                  >
                    {title.trim() || "Notification Title Here"}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: "#cbd5e1", fontSize: "0.82rem", mt: 0.5, lineHeight: 1.3 }}
                  >
                    {message.trim() || "Your message body will appear here on mobile devices like Zomato/Swiggy notifications."}
                  </Typography>

                  {link.trim() && (
                    <Typography variant="caption" sx={{ color: "#38bdf8", mt: 0.8, display: "block" }}>
                      🔗 {link}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Paper>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 1 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
          sx={{
            px: 3,
            py: 1,
            borderRadius: 2,
            fontWeight: "700",
            boxShadow: "0 4px 12px rgba(26, 35, 126, 0.3)",
          }}
        >
          {loading ? "Broadcasting..." : "Send Broadcast"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BroadcastNotificationDialog;
